import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/cn';
import { PaperThumbnailStrip } from './PaperThumbnailStrip';
import type { AutosaveStatus } from './AutosaveIndicator';
import type { SubmissionTrustState } from '../../trust/SubmissionStateBadge';
import type { SubmissionKind } from './ViewerToolbar';
import { ViewerToolbar } from './ViewerToolbar';

type SubmissionViewerProps = {
  loading?: boolean;
  studentName: string;
  assignmentTitle?: string;
  imageUrls: string[];
  kind: SubmissionKind;
  pageIndex: number;
  onPageIndex: (i: number) => void;
  zoomPercent: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  rotationDeg: number;
  onRotate: () => void;
  fitWidth: boolean;
  onToggleFitWidth: () => void;
  annotateEnabled?: boolean;
  annotateOn?: boolean;
  onToggleAnnotate?: () => void;
  autosaveStatus: AutosaveStatus;
  submissionTrustState?: SubmissionTrustState | null;
  submissionSynced?: boolean;
  emptyLabel: string;
  className?: string;
};

function ViewerSkeleton(): ReactElement {
  return (
    <div className="flex animate-pulse flex-col gap-3 p-6" aria-hidden>
      <div className="h-4 w-1/3 rounded bg-slate-200 dark:bg-slate-700" />
      <div className="h-[min(60vh,420px)] w-full rounded-dg-card bg-slate-100 dark:bg-slate-800" />
    </div>
  );
}

export function SubmissionViewer({
  loading = false,
  studentName,
  assignmentTitle,
  imageUrls,
  kind,
  pageIndex,
  onPageIndex,
  zoomPercent,
  onZoomIn,
  onZoomOut,
  rotationDeg,
  onRotate,
  fitWidth,
  onToggleFitWidth,
  annotateEnabled,
  annotateOn,
  onToggleAnnotate,
  autosaveStatus,
  submissionTrustState = null,
  submissionSynced = false,
  emptyLabel,
  className,
}: SubmissionViewerProps): ReactElement {
  const { t } = useTranslation();
  const safeIndex = Math.min(Math.max(0, pageIndex), Math.max(0, imageUrls.length - 1));
  const src = imageUrls[safeIndex];

  return (
    <section
      className={cn(
        'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-dg-card border border-slate-200/80 bg-dg-bg-muted dark:border-slate-700/60 dark:bg-slate-900/40',
        className
      )}
      aria-label={t('audit.studio.detailAria')}
    >
      <ViewerToolbar
        studentName={studentName}
        assignmentTitle={assignmentTitle}
        kind={kind}
        zoomPercent={zoomPercent}
        onZoomIn={onZoomIn}
        onZoomOut={onZoomOut}
        onRotate={onRotate}
        fitWidth={fitWidth}
        onToggleFitWidth={onToggleFitWidth}
        annotateEnabled={annotateEnabled}
        annotateOn={annotateOn}
        onToggleAnnotate={onToggleAnnotate}
        autosaveStatus={autosaveStatus}
        submissionTrustState={submissionTrustState}
        submissionSynced={submissionSynced}
      />
      {loading ? (
        <ViewerSkeleton />
      ) : imageUrls.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-8 text-center text-dg-body text-slate-500 dark:text-slate-400">
          {emptyLabel}
        </div>
      ) : (
        <>
          <PaperThumbnailStrip urls={imageUrls} activeIndex={safeIndex} onSelect={onPageIndex} />
          <div className="min-h-0 flex-1 overflow-auto bg-slate-100/80 p-4 dark:bg-slate-950/40">
            <div
              className={cn(
                'mx-auto flex min-h-[200px] justify-center',
                fitWidth ? 'w-full' : 'w-max max-w-full'
              )}
            >
              <img
                src={src}
                alt={t('audit.gradeStudio.submissionImageAlt', { name: studentName })}
                className={cn(
                  'rounded-lg bg-white shadow-dg-card transition-transform duration-200 ease-out',
                  fitWidth
                    ? 'h-auto w-full max-w-full object-contain'
                    : 'max-h-[min(70vh,640px)] w-auto max-w-none object-contain'
                )}
                style={{
                  transform: `scale(${zoomPercent / 100}) rotate(${rotationDeg}deg)`,
                  transformOrigin: 'center center',
                }}
              />
            </div>
          </div>
        </>
      )}
    </section>
  );
}
